import type { ExceptionalDate } from '@gql-generated/graphql'
import type { Moment } from 'moment'
import moment from 'moment'
import type { DayOpeningHours } from '../types.tmp'
import { formatOpeningHourIntervalPart } from './pointOfSale.formatting'

export function mapStringOpeningHourToMoment({
  dayOfWeek,
  stringHour = '00:00',
}: {
  dayOfWeek: number
  stringHour?: string
}): Moment {
  if (!/^\d{2}:\d{2}$/.test(stringHour)) {
    console.error('Invalid "stringHour" format')
  }

  const hoursMoment = moment(stringHour, 'HH:mm')

  // Create a date starting from the current week and define that Monday is
  // the first day of the week.
  return moment()
    .startOf('isoWeek')
    .day(dayOfWeek)
    .hours(hoursMoment.hours())
    .minutes(hoursMoment.minutes())
}

type OpeningStatusOpen = {
  status: 'open'
}

type OpeningStatusExceptionallyOpen = {
  status: 'exceptionally_open'
}

type OpeningStatusClosed = {
  status: 'closed'
  reopeningAt: string | null
}

type OpeningStatusExceptionallyClosed = {
  status: 'exceptionally_closed'
}

type OpeningStatusClosingSoon = {
  status: 'closing_soon'
}

type OpeningStatusNotBusinessDay = {
  status: 'not_business_day'
}

export type OpeningStatus =
  | OpeningStatusOpen
  | OpeningStatusExceptionallyOpen
  | OpeningStatusClosed
  | OpeningStatusExceptionallyClosed
  | OpeningStatusClosingSoon
  | OpeningStatusNotBusinessDay

export function mapDayOpeningHoursToOpeningStatus(
  openingHours: DayOpeningHours | DayOpeningHours[],
  exceptionalDates: ExceptionalDate[],
): OpeningStatus | null {
  const dayOpeningHours = Array.isArray(openingHours)
    ? openingHours?.find((dayOpeningHours) => dayOpeningHours.dayOfWeek === moment().isoWeekday())
    : openingHours

  if (!dayOpeningHours) {
    return null
  }

  // Weekend days.
  if (!dayOpeningHours.intervals || dayOpeningHours.intervals.length < 1) {
    return { status: 'not_business_day' }
  }

  const exceptionalStatus = exceptionalDates.reduce<OpeningStatus | null>(
    (acc, exceptionalDate) => {
      if (acc) return acc

      const day = mapStringOpeningHourToMoment({ dayOfWeek: dayOpeningHours.dayOfWeek })
      const isTodayExceptionalDate = moment(exceptionalDate.date).isSame(day, 'day')

      if (!isTodayExceptionalDate) return null

      // TODO: Manage the case where the day is exceptionally open when the schema returns the interval of opening hours.
      return exceptionalDate.isClosed ? { status: 'exceptionally_closed' } : null
    },
    null,
  )

  if (!!exceptionalStatus) {
    return exceptionalStatus
  }

  const isNotToday = !moment().isSame(
    mapStringOpeningHourToMoment({ dayOfWeek: dayOpeningHours.dayOfWeek }),
    'day',
  )

  if (isNotToday) {
    return null
  }

  for (const intervals of dayOpeningHours.intervals) {
    const startMoment = mapStringOpeningHourToMoment({
      dayOfWeek: dayOpeningHours.dayOfWeek,
      stringHour: intervals.begin,
    })
    const endMoment = mapStringOpeningHourToMoment({
      dayOfWeek: dayOpeningHours.dayOfWeek,
      stringHour: intervals.end,
    })

    // Need to be tested first because the closing soon status is a subset of the open status.
    if (moment().isBetween(endMoment.clone().subtract(1, 'hour'), endMoment)) {
      return { status: 'closing_soon' }
    }

    if (moment().isBetween(startMoment, endMoment)) {
      return { status: 'open' }
    }
  }

  const reopeningAt = dayOpeningHours.intervals.reduce<OpeningStatus | null>((acc, intervals) => {
    if (acc) return acc

    const startMoment = mapStringOpeningHourToMoment({
      dayOfWeek: dayOpeningHours.dayOfWeek,
      stringHour: intervals.begin,
    })

    if (moment().isBefore(startMoment)) {
      return {
        status: 'closed',
        reopeningAt: formatOpeningHourIntervalPart({
          dayOfWeek: dayOpeningHours.dayOfWeek,
          stringHour: intervals.begin,
        }),
      }
    }

    return null
  }, null)

  return reopeningAt ?? { status: 'closed', reopeningAt: null }
}
